import { useCreateGroup, useMyGroups } from '@hobbytools/data';
import { Icon } from '@hobbytools/icons';
import { type AppId, AppShell, apps, appUrl, friendlyError, useAuth } from '@hobbytools/platform';
import { Banner, Button, Card, Divider, TextField } from '@hobbytools/ui';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { linkClass } from './AuthLayout';

function SignedOut() {
  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <h1 className="heading-display text-3xl">Game night, written down.</h1>
      <p className="text-lg text-fg-muted">
        Hobby Tools keeps your group's narrative campaigns: forces, battles and the story they add up to.
      </p>
      <div className="flex flex-wrap gap-3">
        <Link to="/sign-up" className="contents">
          <Button>Create an account</Button>
        </Link>
        <Link to="/sign-in" className="contents">
          <Button variant="secondary">Sign in</Button>
        </Link>
      </div>
    </div>
  );
}

function JoinWithCode() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.trim()) navigate(`/join/${code.trim().toUpperCase()}`);
  };
  return (
    <form onSubmit={submit} className="flex items-end gap-2">
      <TextField label="Have an invite code?" value={code} onChange={setCode} className="flex-1" />
      <Button type="submit" variant="secondary" isDisabled={!code.trim()}>
        Continue
      </Button>
    </form>
  );
}

function CreateGroup() {
  const create = useCreateGroup();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const id = await create.mutateAsync({ name: name.trim() });
      navigate(`/groups/${id}`);
    } catch (err) {
      setError(friendlyError(err));
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-2">
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className="flex items-end gap-2">
        <TextField
          label="Start a new group"
          description="A group is your gaming circle: a store night, a club, the usual suspects."
          value={name}
          onChange={setName}
          maxLength={80}
          className="flex-1"
        />
      </div>
      <Button type="submit" className="self-start" isDisabled={create.isPending || !name.trim()}>
        {create.isPending ? 'Creating…' : 'Create group'}
      </Button>
    </form>
  );
}

function SignedIn({ userId, displayName }: { userId: string; displayName: string }) {
  const groups = useMyGroups(userId);
  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="eyebrow">Welcome back</p>
        <h1 className="heading-display text-3xl">{displayName}</h1>
      </header>

      <section aria-labelledby="groups-heading" className="flex flex-col gap-3">
        <h2 id="groups-heading" className="heading-display text-xl">
          Your groups
        </h2>
        {groups.isLoading ? (
          <p className="text-fg-muted">Loading your groups…</p>
        ) : groups.error ? (
          <Banner tone="danger">{friendlyError(groups.error)}</Banner>
        ) : groups.data?.length ? (
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-3">
            {groups.data.map((g) => (
              <li key={g.id}>
                <Link
                  to={`/groups/${g.id}`}
                  className="block rounded-lg focus-visible:outline-2 focus-visible:outline-border-focus"
                >
                  <Card className="flex h-full flex-col gap-1 hover:border-border-strong">
                    <span className="font-semibold">{g.name}</span>
                    <span className="text-sm text-fg-muted">
                      {g.myStatus === 'pending'
                        ? 'Waiting for approval'
                        : g.myRole === 'member'
                          ? 'Member'
                          : g.myRole === 'admin'
                            ? 'Admin'
                            : 'Owner'}
                    </span>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-fg-muted">
            No groups yet. Start one below, or join with an invite code from a friend.
          </p>
        )}
      </section>

      <Divider ornament />

      <section className="grid gap-6 md:grid-cols-2">
        <CreateGroup />
        <JoinWithCode />
      </section>

      <section aria-labelledby="systems-heading" className="flex flex-col gap-3">
        <h2 id="systems-heading" className="heading-display text-xl">
          Game systems
        </h2>
        <ul className="flex flex-wrap gap-3">
          {apps
            .filter((a) => a.id !== 'hub')
            .map((a) =>
              a.enabled ? (
                <li key={a.id}>
                  <a className={`${linkClass} flex items-center gap-1`} href={appUrl(a.id as AppId)}>
                    <Icon name="swords" /> {a.name}
                  </a>
                </li>
              ) : (
                <li key={a.id} className="text-fg-subtle">
                  {a.name} (on the roadmap)
                </li>
              ),
            )}
        </ul>
      </section>
    </div>
  );
}

export function Home() {
  const { user, isLoading } = useAuth();
  return (
    <AppShell current="hub">
      {isLoading ? null : user ? (
        <SignedIn
          userId={user.id}
          displayName={(user.user_metadata?.display_name as string | undefined) ?? user.email ?? 'Player'}
        />
      ) : (
        <SignedOut />
      )}
    </AppShell>
  );
}
